import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Check, ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { api } from "@/lib/api-client";
import { toast } from "@/components/ui/Toast";
import { Button } from "@/components/ui/Button";
import { useTranslation } from "@/hooks/useTranslation";
import { pickLocalized } from "@/lib/localized";
import { QuestionCard, type QuestionCardProps } from "./QuestionCard";

// ─────────────────────────────────────────────
// DomainTab
//
// Renders all posture sections for one domain tab.
// Auto-saves responses via debounced PATCH /assessments/:id/responses.
// Design: §6.3 Domain Tabs — questionnaire fill
// ─────────────────────────────────────────────

export interface PostureWithQuestions {
  postureId: string;
  postureName: string;
  postureNameSr?: string;
  postureCode: string;
  questions: Omit<QuestionCardProps, "index">[];
}

interface DomainTabProps {
  assessmentId: string;
  postures: PostureWithQuestions[];
  /** Current server responses keyed by questionId, used to preserve existing values/comments when only one field changes. */
  responses: Record<string, { value: unknown; comment: string | null }>;
  readOnly?: boolean;
  /** Show a banner explaining the domain is read-only due to role restrictions (not status). */
  showReadOnlyBanner?: boolean;
  onResponseChange?: (questionId: string, value: unknown, comment: string | null) => void;
  /** Register an awaitable flush of the pending autosave queue with the parent,
   *  so "Submit for Review" can drain it before snapshotting (S-094). Called with
   *  the flush fn on mount and null on unmount. */
  onRegisterFlush?: (flush: (() => Promise<void>) | null) => void;
  onPrevDomain?: () => void;
  onNextDomain?: () => void;
  currentDomainIndex?: number;
  totalDomains?: number;
  /** S-105: which posture to open on mount — 'first' (default, for forward
   *  entry) or 'last' when the user navigated BACK into this domain from the
   *  next one, so "Previous Section" lands on the domain's last posture. */
  initialPosture?: "first" | "last";
}

const DEBOUNCE_MS = 1500;

export function DomainTab({
  assessmentId,
  postures,
  responses,
  readOnly,
  showReadOnlyBanner,
  onResponseChange,
  onRegisterFlush,
  onPrevDomain,
  onNextDomain,
  currentDomainIndex,
  totalDomains,
  initialPosture = "first",
}: DomainTabProps) {
  const { language } = useTranslation();
  const { t } = useTranslation();
  // Pending queue: questionId -> { value, comment }
  const pendingRef = useRef<Map<string, { value: unknown; comment: string | null }>>(new Map());
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Serializes saves so two PATCHes never race, and lets submit await the drain.
  const inFlightRef = useRef<Promise<void> | null>(null);

  const mutation = useMutation({
    mutationFn: (pendingResponses: { questionId: string; value: unknown; comment?: string }[]) =>
      api.patch(`/assessments/${assessmentId}/responses`, { responses: pendingResponses }),
    onError: () => {
      toast.error(t("assessment.autoSaveFailedToast"));
    },
  });

  // S-093: keep queued answers until the save SUCCEEDS. On failure they stay in
  // the queue (surfaced via Retry) instead of being dropped before the request,
  // so a transient error never silently loses answers the screen still shows.
  const flushPending = useCallback(async (): Promise<void> => {
    // One save at a time — wait for any in-flight save first (submit relies on this).
    if (inFlightRef.current) {
      try { await inFlightRef.current; } catch { /* surfaced via Retry */ }
    }
    const pending = pendingRef.current;
    if (pending.size === 0) return;
    const snapshot = Array.from(pending.entries());
    const payload = snapshot.map(([questionId, { value, comment }]) => ({
      questionId,
      value,
      ...(comment !== null && comment !== undefined ? { comment } : {}),
    }));
    const run = (async () => {
      await mutation.mutateAsync(payload);
      // Success: drop exactly what we saved, unless the user re-edited it mid-flight
      // (handlers always store a fresh object, so identity tells them apart).
      for (const [questionId, saved] of snapshot) {
        if (pendingRef.current.get(questionId) === saved) pendingRef.current.delete(questionId);
      }
    })();
    inFlightRef.current = run;
    try {
      await run;
    } finally {
      if (inFlightRef.current === run) inFlightRef.current = null;
    }
  }, [mutation]);

  // Always invoke the latest flushPending from stable callbacks (timer/unmount/parent).
  const flushRef = useRef(flushPending);
  flushRef.current = flushPending;

  // S-094: register an awaitable flush with the parent so "Submit for Review"
  // can drain the debounced queue before it snapshots the immutable version.
  useEffect(() => {
    onRegisterFlush?.(() => flushRef.current());
    return () => {
      onRegisterFlush?.(null);
      if (timerRef.current) clearTimeout(timerRef.current);
      void flushRef.current().catch(() => {});
    };
  }, [onRegisterFlush]);

  function scheduleFlush() {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => { void flushRef.current().catch(() => {}); }, DEBOUNCE_MS);
  }

  function handleValueChange(questionId: string, value: unknown) {
    const pending = pendingRef.current.get(questionId);
    // Prefer pending comment, fall back to last-saved comment from server
    const comment = pending?.comment !== undefined ? pending.comment : (responses[questionId]?.comment ?? null);
    pendingRef.current.set(questionId, { value, comment });
    onResponseChange?.(questionId, value, comment);
    scheduleFlush();
  }

  function handleCommentChange(questionId: string, comment: string) {
    const pending = pendingRef.current.get(questionId);
    // Prefer pending value, fall back to last-saved value from server — never send null if a value exists
    const existingValue = pending !== undefined ? pending.value : (responses[questionId]?.value ?? null);
    pendingRef.current.set(questionId, { value: existingValue, comment });
    onResponseChange?.(questionId, existingValue, comment);
    scheduleFlush();
  }

  const hasPostureSections = postures.length >= 1;
  const [activePostureId, setActivePostureId] = useState<string | null>(
    // S-105: enter at the last posture when the user navigated BACK into this
    // domain (the Radix tab remounts DomainTab, so this initializer decides the
    // landing posture). The effect below only corrects invalid ids, so a valid
    // last-posture id survives.
    (initialPosture === "last" ? postures[postures.length - 1] : postures[0])?.postureId ?? null,
  );

  useEffect(() => {
    // Keep activePostureId valid when the postures list changes.
    if (!postures.find((p) => p.postureId === activePostureId)) {
      setActivePostureId(postures[0]?.postureId ?? null);
    }
  }, [postures, activePostureId]);

  const postureProgress = useMemo(() => {
    return postures.map((p) => {
      const scored = p.questions.filter((q) => q.type !== "OPEN_ENDED");
      const answered = scored.filter((q) => {
        const v = q.value;
        if (v === null || v === undefined) return false;
        if (q.type === "MULTI_SUB_FIELD") {
          const r = v as Record<string, string>;
          return Object.values(r).some((x) => x !== "");
        }
        return typeof v === "string" ? v.trim() !== "" : true;
      }).length;
      return {
        postureId: p.postureId,
        answered,
        total: scored.length,
        isComplete: scored.length > 0 && answered === scored.length,
      };
    });
  }, [postures]);

  if (postures.length === 0) {
    return (
      <div className="py-16 text-center">
        <p className="text-[14px] text-text-muted">{t("assessment.noQuestionsInDomain")}</p>
      </div>
    );
  }

  const activePosture = hasPostureSections
    ? postures.find((p) => p.postureId === activePostureId) ?? postures[0]!
    : postures[0]!;

  const activeQuestionsWithHandlers = activePosture.questions.map((q) => ({
    ...q,
    readOnly: readOnly || q.readOnly,
    onValueChange: handleValueChange,
    onCommentChange: handleCommentChange,
  }));

  const activePostureIndex = Math.max(
    0,
    postures.findIndex((p) => p.postureId === activePosture.postureId),
  );
  const isFirstPosture = activePostureIndex === 0;
  const isLastPosture = activePostureIndex === postures.length - 1;
  const isFirstDomain = (currentDomainIndex ?? 0) === 0;
  const isLastDomain =
    totalDomains !== undefined && currentDomainIndex === totalDomains - 1;
  const canGoPrev = !isFirstPosture || !isFirstDomain;
  const canGoNext = !isLastPosture || !isLastDomain;

  const handlePrev = () => {
    if (!isFirstPosture) {
      setActivePostureId(postures[activePostureIndex - 1]!.postureId);
      return;
    }
    onPrevDomain?.();
  };
  const handleNext = () => {
    if (!isLastPosture) {
      setActivePostureId(postures[activePostureIndex + 1]!.postureId);
      return;
    }
    onNextDomain?.();
  };

  const showNavFooter =
    postures.length > 1 || (totalDomains !== undefined && totalDomains > 1);
  const navFooter = showNavFooter ? (
    <div className="flex items-center justify-between pt-4">
      <Button variant="outline" onClick={handlePrev} disabled={!canGoPrev}>
        <ChevronLeft className="mr-1.5 h-4 w-4" />
        {t("assessment.previousSection")}
      </Button>
      <span className="text-caption text-text-muted">
        {activePostureIndex + 1} {t("common.of")} {postures.length}
      </span>
      <Button variant="outline" onClick={handleNext} disabled={!canGoNext}>
        {t("assessment.nextSection")}
        <ChevronRight className="ml-1.5 h-4 w-4" />
      </Button>
    </div>
  ) : null;

  const savingIndicator = mutation.isPending ? (
    <div className="flex items-center gap-2 text-[13px] text-text-muted">
      <span className="h-3 w-3 animate-spin rounded-full border border-current/30 border-t-current" />
      {t("common.saving")}
    </div>
  ) : mutation.isError ? (
    // S-093: never a silent drop — the failed answers are still queued; Retry re-sends them.
    <div
      className="flex items-center gap-2 rounded-control px-3 py-1.5 text-[13px]"
      style={{ color: "var(--danger)", backgroundColor: "color-mix(in srgb, var(--danger) 8%, transparent)" }}
    >
      <span>{t("assessment.autoSaveFailedInline")}</span>
      <Button
        variant="outline"
        size="sm"
        onClick={() => { void flushRef.current().catch(() => {}); }}
      >
        {t("common.retry")}
      </Button>
    </div>
  ) : null;

  return (
    <div className="flex h-full min-h-0 flex-col gap-6">
      {showReadOnlyBanner && (
        <div
          className="flex items-start gap-3 rounded-card border px-4 py-3 text-[13px]"
          style={{
            backgroundColor: "var(--warning-bg, color-mix(in srgb, var(--warning, #f59e0b) 12%, transparent))",
            borderColor: "var(--warning-border, color-mix(in srgb, var(--warning, #f59e0b) 30%, transparent))",
            color: "var(--text-secondary)",
          }}
        >
          <span
            className="mt-0.5 h-4 w-4 flex-shrink-0 rounded-full text-center text-[10px] font-bold leading-4"
            style={{ backgroundColor: "var(--warning, #f59e0b)", color: "#fff" }}
          >
            !
          </span>
          <span>
            {t("assessment.readOnlyDomainBanner")}
          </span>
        </div>
      )}
      {hasPostureSections ? (
        <div className="flex flex-col gap-6 lg:min-h-0 lg:flex-1 lg:flex-row lg:items-stretch">
          {/* Sections sidebar — locked to the tab content height; never
              scrolls the page, but scrolls internally if the section list is long. */}
          <aside
            className={cn(
              "w-full rounded-card border border-border-default bg-card p-5",
              "lg:w-[228px] lg:flex-shrink-0 lg:h-full lg:overflow-y-auto",
            )}
          >
            <p className="mb-3 text-[11px] font-semibold uppercase tracking-[1.5px] text-text-muted">
              {t("vendorPortal.sections")}
            </p>
            <ul className="flex flex-col gap-1">
              {postures.map((p) => {
                const prog = postureProgress.find((x) => x.postureId === p.postureId);
                const isActive = p.postureId === activePosture.postureId;
                const isDone = prog?.isComplete ?? false;
                return (
                  <li key={p.postureId}>
                    <button
                      type="button"
                      onClick={() => setActivePostureId(p.postureId)}
                      className={cn(
                        "group flex w-full items-start gap-2.5 rounded-control px-2 py-1.5 text-left transition-colors",
                        isActive
                          ? "text-accent-text"
                          : "text-text-secondary hover:text-text-primary",
                      )}
                    >
                      <span
                        className={cn(
                          "mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full border transition-colors",
                          isDone
                            ? "border-success bg-success text-white"
                            : isActive
                              ? "border-accent"
                              : "border-border-default",
                        )}
                      >
                        {isDone ? (
                          <Check className="h-2.5 w-2.5" strokeWidth={3} />
                        ) : isActive ? (
                          <span className="h-1.5 w-1.5 rounded-full bg-accent" />
                        ) : null}
                      </span>
                      <span className="text-[13px] leading-snug">{pickLocalized(p.postureName, p.postureNameSr, language)}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </aside>

          {/* Active section content — scrolls inside the tab. QuestionCard
              renders its own bordered card, so no wrapper needed here. */}
          <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-3 lg:overflow-y-auto lg:pr-1">
            {activeQuestionsWithHandlers.map((q, i) => (
              <QuestionCard key={q.questionId} {...q} index={i + 1} />
            ))}
            {savingIndicator}
            {navFooter}
          </div>
        </div>
      ) : (
        <>
          <div className="flex flex-col gap-3">
            {activeQuestionsWithHandlers.map((q, i) => (
              <QuestionCard key={q.questionId} {...q} index={i + 1} />
            ))}
          </div>
          {savingIndicator}
          {navFooter}
        </>
      )}
    </div>
  );
}
